import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertOctagon, Zap, UserPlus, Trash2, CalendarClock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "What am I avoiding?" card
 *
 * Shows tasks rolled over 3+ days and forces a real decision:
 *   Do Now | Delegate | Delete | Defer + plan
 * Only renders when there are stale tasks — otherwise the user's dashboard
 * stays clean.
 */
export default function StaleTasksCard({ onResolved }) {
  const [stale, setStale] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deferring, setDeferring] = useState(null); // task object being deferred
  const [delegating, setDelegating] = useState(null); // task object being delegated
  const [assignee, setAssignee] = useState("");
  const [plan, setPlan] = useState("");
  const [newPriority, setNewPriority] = useState("ThisWeek");
  const [working, setWorking] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks/stale`, { withCredentials: true });
      setStale(res.data.tasks || []);
    } catch (err) {
      console.error("Failed to load stale tasks", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (task, payload) => {
    setWorking(task.task_id);
    try {
      await axios.post(
        `${API}/tasks/${task.task_id}/resolve-stale`,
        payload,
        { withCredentials: true }
      );
      toast.success(`${payload.action.replace("_", " ")} — ${task.description.slice(0, 40)}`);
      setStale((s) => s.filter((t) => t.task_id !== task.task_id));
      onResolved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resolve");
    } finally {
      setWorking(null);
      setDeferring(null);
      setDelegating(null);
      setPlan("");
      setAssignee("");
    }
  };

  if (loading || stale.length === 0) return null;

  return (
    <>
      <Card
        className="bento-card border-amber-500/40 bg-gradient-to-br from-amber-500/5 to-red-500/5"
        data-testid="stale-tasks-card"
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertOctagon className="w-5 h-5 text-amber-400" />
            What are you avoiding?
            <Badge className="ml-1 bg-amber-500/20 text-amber-300 border-amber-500/40">
              {stale.length}
            </Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            These have rolled over {stale[0]?.days_rolled || 3}+ days. Time to decide.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {stale.slice(0, 5).map((t) => {
            const busy = working === t.task_id;
            return (
              <div
                key={t.task_id}
                className="p-3 rounded-lg bg-zinc-900/60 border border-zinc-800"
                data-testid="stale-task-row"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{t.description}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 text-red-300 border-red-500/40">
                        Rolled {t.days_rolled}d
                      </Badge>
                      {t.category && (
                        <span className="text-[10px] text-muted-foreground">{t.category}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => resolve(t, { action: "do_now" })}
                    className="h-8 text-xs border-emerald-500/40 hover:bg-emerald-500/10"
                    data-testid="stale-action-do-now"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Zap className="w-3 h-3 mr-1" /> Do Now</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setDelegating(t)}
                    className="h-8 text-xs border-blue-500/40 hover:bg-blue-500/10"
                    data-testid="stale-action-delegate"
                  >
                    <UserPlus className="w-3 h-3 mr-1" /> Delegate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setDeferring(t)}
                    className="h-8 text-xs border-amber-500/40 hover:bg-amber-500/10"
                    data-testid="stale-action-defer"
                  >
                    <CalendarClock className="w-3 h-3 mr-1" /> Defer
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm(`Delete "${t.description}"?`)) {
                        resolve(t, { action: "delete" });
                      }
                    }}
                    className="h-8 text-xs border-red-500/40 hover:bg-red-500/10 text-red-300"
                    data-testid="stale-action-delete"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
          {stale.length > 5 && (
            <p className="text-xs text-muted-foreground text-center pt-1">
              +{stale.length - 5} more zombie task{stale.length - 5 > 1 ? "s" : ""}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Defer modal — forces a real next step */}
      <Dialog open={!!deferring} onOpenChange={(v) => !v && setDeferring(null)}>
        <DialogContent data-testid="stale-defer-modal">
          <DialogHeader>
            <DialogTitle>Defer "{deferring?.description?.slice(0, 50)}"</DialogTitle>
            <DialogDescription className="sr-only">
              Move this task out of Today with a specific next step.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Where to?</label>
              <div className="flex gap-2 mt-1">
                {["ThisWeek", "ParkingLot"].map((p) => (
                  <Button
                    key={p}
                    size="sm"
                    variant={newPriority === p ? "default" : "outline"}
                    onClick={() => setNewPriority(p)}
                  >
                    {p === "ThisWeek" ? "This Week" : "Parking Lot"}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                Real next step (required — no vague "look into it")
              </label>
              <Input
                data-testid="stale-defer-plan"
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                placeholder="e.g. Send Sarah the draft PPE audit by Friday"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDeferring(null)}>Cancel</Button>
              <Button
                disabled={plan.trim().length < 5}
                onClick={() => resolve(deferring, {
                  action: "defer",
                  new_priority: newPriority,
                  plan: plan.trim(),
                })}
                data-testid="stale-defer-confirm"
              >
                Defer with plan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delegate modal */}
      <Dialog open={!!delegating} onOpenChange={(v) => !v && setDelegating(null)}>
        <DialogContent data-testid="stale-delegate-modal">
          <DialogHeader>
            <DialogTitle>Delegate "{delegating?.description?.slice(0, 50)}"</DialogTitle>
            <DialogDescription className="sr-only">
              Hand this task to someone else with their name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Who's picking this up?</label>
              <Input
                data-testid="stale-delegate-assignee"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                placeholder="e.g. Sarah (CNO), Unit 300 charge nurse"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDelegating(null)}>Cancel</Button>
              <Button
                disabled={!assignee.trim()}
                onClick={() => resolve(delegating, { action: "delegate", assignee: assignee.trim() })}
                data-testid="stale-delegate-confirm"
              >
                Delegate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
